'use client';

import type { InputHTMLAttributes, TextareaHTMLAttributes } from 'react';

/**
 * Every text field on the site (hero, bottom CTA, contact page) shares this file instead of each
 * section re-typing its own border/background/text classes. A change here (e.g. the 16px font size —
 * the site's floor; nothing renders smaller) reaches every field. Two shapes are exported because the
 * sections use two genuinely different
 * layouts, not just different colours:
 *
 *   - `Field`      one bordered pill around one input/textarea (contact page's 3 fields).
 *   - `FieldGroup` one shared bordered pill around 2+ inputs, divided by a border between them
 *                  (hero's name+email pair; the bottom CTA's name+email pair).
 *
 * `variant` picks the colour treatment; both shapes read from the same VARIANT table, so a variant's
 * colours only exist in one place.
 */
export type FieldVariant = 'light' | 'dark' | 'tinted';

const VARIANT = {
  /** Hero forms: white pill on a white page, gray-900 text. */
  light: {
    border: 'border-blue-bell-200',
    borderWidthClass: 'border-2', // 2px, the site-wide field border width (per an explicit request)
    cellBg: 'bg-blue-bell-100',
    outerBg: '',
    text: 'text-gray-900',
    placeholder: 'placeholder:text-blue-bell-600',
    size: 'ds-text-base-normal',
  },
  /** Bottom CTA: translucent pill over a photo background, white text. */
  dark: {
    border: 'border-white-a32',
    borderWidthClass: 'border-2', // 2px, the site-wide field border width (per an explicit request)
    cellBg: '',
    outerBg: 'bg-white-a32 backdrop-blur-sm',
    text: 'text-white',
    placeholder: 'placeholder:text-white/[0.64]',
    size: 'ds-text-base-medium',
  },
  /** Contact page: pale lavender fill, tertiary-text placeholder. */
  tinted: {
    border: 'border-border-primary',
    borderWidthClass: 'border-2', // 2px, the site-wide field border width (per an explicit request)
    cellBg: 'bg-background-lighter',
    outerBg: '',
    text: 'text-text-primary',
    placeholder: 'placeholder:text-text-tertiary',
    size: 'ds-text-base-normal',
  },
} as const satisfies Record<FieldVariant, {
  border: string;
  borderWidthClass: string;
  cellBg: string;
  outerBg: string;
  text: string;
  placeholder: string;
  size: string;
}>;

/** Shared "what does the typed text look like" class list — every field, every section, 14px. */
function controlClassName(variant: FieldVariant, align: 'center' | 'left', extra = ''): string {
  const v = VARIANT[variant];
  return [
    'w-full min-w-0 bg-transparent outline-none focus:outline-none focus:ring-0',
    v.size,
    v.text,
    v.placeholder,
    align === 'center' ? 'text-center' : 'text-left',
    extra,
  ]
    .filter(Boolean)
    .join(' ');
}

type FieldBaseProps = {
  variant?: FieldVariant;
  align?: 'center' | 'left';
  height?: number;
  className?: string;
};

export type FieldProps = FieldBaseProps &
  (
    | ({ multiline?: false } & InputHTMLAttributes<HTMLInputElement>)
    | ({ multiline: true } & TextareaHTMLAttributes<HTMLTextAreaElement>)
  );

/** One self-contained bordered field: outer pill (border, hover/focus colour) + inner tinted box + control. */
export function Field({
  variant = 'light',
  align = 'center',
  height = 56,
  className = '',
  multiline,
  ...rest
}: FieldProps) {
  const v = VARIANT[variant];
  return (
    <div
      className={`flex w-full shrink-0 items-stretch overflow-hidden rounded-12 ${v.borderWidthClass} ${v.border} p-1 transition-colors duration-200 hover:border-prople-500 focus-within:border-prople-500 ${className}`}
      style={{ height }}
    >
      <div className={`flex w-full min-w-0 flex-1 rounded-8 ${v.cellBg}`}>
        {multiline ? (
          <textarea
            {...(rest as TextareaHTMLAttributes<HTMLTextAreaElement>)}
            className={controlClassName(variant, align, 'resize-none px-3 py-3')}
          />
        ) : (
          <input
            {...(rest as InputHTMLAttributes<HTMLInputElement>)}
            className={controlClassName(variant, align, 'h-full px-3')}
          />
        )}
      </div>
    </div>
  );
}

export type FieldGroupField = InputHTMLAttributes<HTMLInputElement> & { key: string };

export type FieldGroupProps = {
  variant: FieldVariant;
  fields: FieldGroupField[];
  /** Hero's pill has an extra padded white box wrapping the divided cells; the CTA's does not. */
  nested?: boolean;
  /** True: stacked (divider on the bottom) below `sm`, side-by-side (divider on the right) from `sm` up.
   * False/omitted: always side-by-side. Written as literal `sm:` classes below, not built from this
   * flag at runtime — Tailwind's scanner needs the full class name to appear verbatim in this file. */
  stacked?: boolean;
  /** Explicit height for the row of fields (not the outer pill, which just hugs it plus its own padding). */
  height?: number;
  className?: string;
};

/** The shared pill for two (or more) fields divided by a single border between them. */
export function FieldGroup({
  variant,
  fields,
  nested = false,
  stacked = false,
  height,
  className = '',
}: FieldGroupProps) {
  const v = VARIANT[variant];
  const rowDirection = stacked ? 'flex-col sm:flex-row sm:items-center' : 'flex-row items-center';

  return (
    <div
      className={`flex w-full shrink-0 overflow-hidden rounded-12 border-2 ${v.border} ${v.outerBg} transition-colors duration-200 hover:border-prople-500 focus-within:border-prople-500 ${nested ? 'gap-[3px] p-[3px]' : ''} ${className}`}
    >
      <div
        className={`flex ${rowDirection} w-full overflow-hidden ${nested ? 'rounded-10 bg-white gap-[3px]' : ''}`}
        style={height ? { height } : undefined}
      >
        {fields.map(({ key, className: fieldClassName, ...field }, i) => {
          const isLast = i === fields.length - 1;
          const divider = isLast ? '' : stacked ? `border-b-2 ${v.border} sm:border-b-0 sm:border-r-2` : `border-r-2 ${v.border}`;
          return (
            <div key={key} className={`flex h-full min-w-0 flex-1 items-center ${divider} ${v.cellBg}`}>
              <input {...field} className={controlClassName(variant, 'center', fieldClassName)} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
